import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 紧凑卡片样式 - 2列布局，优化设计细节
const EventCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "12px",
  background: theme.palette.mode === "dark" 
    ? "rgba(28, 28, 30, 0.4)" 
    : "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: theme.palette.mode === "dark"
    ? "0.5px solid rgba(84, 84, 88, 0.5)"
    : "0.5px solid rgba(0, 0, 0, 0.08)",
  cursor: "pointer",
  transition: "all 0.2s ease",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: theme.palette.mode === "dark"
    ? "0 2px 8px rgba(0, 0, 0, 0.2)"
    : "0 2px 8px rgba(0, 0, 0, 0.05)",
  "&:active": {
    transform: "scale(0.97)",
    background: theme.palette.mode === "dark" 
      ? "rgba(28, 28, 30, 0.6)" 
      : "rgba(255, 255, 255, 0.8)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 4px 12px rgba(0, 0, 0, 0.3)"
      : "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

const MobileHome = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error("获取数据失败:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 计算倒计时（用于卡片显示）
  const calculateCountdown = (eventTime) => {
    if (!eventTime) return null;
    const now = new Date();
    const time = new Date(eventTime);
    const diff = time - now;
    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return { days, hours };
    if (hours > 0) return { hours, minutes };
    return { minutes };
  };

  const formatCountdown = (countdown) => {
    if (!countdown) return null;
    if (countdown.days) return `${countdown.days}天${countdown.hours}时`;
    if (countdown.hours) return `${countdown.hours}时${countdown.minutes}分`;
    return `${countdown.minutes}分`;
  };

  const handleEventClick = (event) => {
    navigate(`/event/${event.id}`);
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  return (
    <Box sx={{ px: 2, py: 2.5 }}>
      {/* 主标题区域 - 优化设计 */}
      <Box sx={{ mb: 3.5 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: textColor,
            mb: 1.25,
            lineHeight: 1.25,
            letterSpacing: "-0.02em",
          }}
        >
          欢迎来到3CCA基督徒平台
        </Typography>
        <Typography
          sx={{
            fontSize: "0.9rem",
            color: secondaryTextColor,
            lineHeight: 1.6,
            fontWeight: 400,
            letterSpacing: "0.01em",
          }}
        >
          在这里，我们将遇见基督，践行召命，生命更新
        </Typography>
      </Box>

      {/* 最新动态区域 - 2列网格布局 */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontSize: "1.1rem",
            fontWeight: 600,
            color: textColor,
            mb: 2.5,
            letterSpacing: "-0.01em",
          }}
        >
          最新动态
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={20} sx={{ color: textColor }} />
          </Box>
        ) : events.length > 0 ? (
          <Grid container spacing={1.5}>
            {events.slice(0, 6).map((event) => {
              const countdown = calculateCountdown(event.time);
              const formattedCountdown = formatCountdown(countdown);
              return (
                <Grid item xs={6} key={event.id}>
                  <EventCard onClick={() => handleEventClick(event)}>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: textColor,
                        mb: 0.75,
                        lineHeight: 1.35,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "2.3em",
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: secondaryTextColor,
                        mb: 0.875,
                        lineHeight: 1.45,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        flex: 1,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {event.description}
                    </Typography>
                    <Box sx={{ mt: "auto", pt: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: "0.7rem", color: secondaryTextColor }} />
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: secondaryTextColor,
                            lineHeight: 1.2,
                            letterSpacing: "0.01em",
                          }}
                        >
                          {event.displayTime.split(" ")[0]}
                        </Typography>
                      </Box>
                      {formattedCountdown && (
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#0a84ff",
                            letterSpacing: "0.01em",
                          }}
                        >
                          {formattedCountdown}
                        </Typography>
                      )}
                    </Box>
                  </EventCard>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem" }}>
              暂无活动数据
            </Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default MobileHome;
